// GAVION ai-email: AI drafting assistant (spec 2026-10-14-ai-email-assistant-design).
export const aiEn = {
  compose: {
    draft: "✨ Draft with AI",
    regenerate: "Regenerate",
    drafting: "Drafting…",
    instruction: "Instruction (optional)",
    instruction_placeholder: "follow up on the quote, shorter…",
    replace_title: "Replace the current text?",
    replace_body: "The AI draft replaces the subject and the message.",
    rationale: "Why this email: %{text}",
  },
  placeholder_left: "Replace « [to fill in] » before sending.",
  errors: {
    unavailable: "AI draft unavailable right now. Try again later.",
    cap: "Daily AI draft limit reached. Try again tomorrow.",
    not_configured: "The AI assistant is not configured yet (missing key).",
    unsupported_claim:
      "Draft refused: it contains « %{detail} », which is not in the history.",
    unsupported_claim_generic:
      "Draft refused: it contains a figure or a promise that is not in the history.",
    forbidden: "Access denied.",
    not_found: "Lead not found.",
    not_pending:
      "This draft is no longer available (replaced, expired or already sent).",
  },
  dialer_toast: "A follow-up draft will be ready in a few minutes.",
  drafts: {
    title: "Drafts",
    proposed: "Proposed draft",
    empty: "No draft to approve.",
    to: "To: %{email}",
    send: "Send",
    edit: "Edit",
    reject: "Reject",
    confirm_title: "Send this email?",
    confirm_send: "Send",
    cancel: "Cancel",
    sent: "Email sent.",
    rejected: "Draft rejected.",
    reject_title: "Reject the draft",
    reject_reason: "Reason (optional)",
    reject_confirm: "Reject",
    reasons: {
      not_relevant: "Not relevant",
      wrong_tone: "Wrong tone",
      factual_error: "Factual error",
      other: "Other",
    },
    triggers: {
      after_call: "After a call",
      client_reply: "Client reply",
      after_demo: "After the demo",
      stale_lead: "Lead gone quiet",
      on_demand: "On demand",
      follow_up: "AI follow-up",
    },
    rep_filter: "Rep",
    mine: "Mine",
    all_reps: "All",
    dashboard: "Drafts to approve (%{smart_count})",
    badge:
      "%{smart_count} draft to approve |||| %{smart_count} drafts to approve",
    no_recipient: "No recipient: use Edit.",
    placeholder_block:
      "This draft contains « [to fill in] »: use Edit to complete it.",
  },
  // GAVION ai follow-up (spec 2026-10-20-ai-follow-up-agent-design)
  follow_up: {
    title: "AI follow-up",
    touch: "Touch %{position} of %{total}",
    touch_label: "Follow-up — touch %{position} of %{total}",
    situations: {
      voicemail: "Voicemail left",
      callback: "Callback agreed",
      brochure: "Brochure sent",
      demo: "Demo",
      campaign: "Email campaign", // GAVION video-campaign
    },
    angles: {
      recap: "Recap",
      value: "Value",
      proof: "Proof",
      question: "Question",
      pre_callback: "Before the callback",
      breakup: "Last message",
    },
    plan_status: { active: "Active", done: "Done", stopped: "Stopped" },
    touch_status: {
      planned: "Planned",
      drafting: "Being written",
      proposed: "To approve",
      sent: "Sent",
      skipped: "Skipped",
      cancelled: "Cancelled",
    },
    stop_reasons: {
      replaced: "Replaced by a new follow-up.",
      new_outcome: "Stopped: a new call had another outcome.",
      reply: "Stopped: the prospect replied.",
      unsubscribe: "Stopped: the prospect unsubscribed.",
      stop_request: "Stopped: the prospect asked for no more emails.",
      bounce: "Stopped: the email bounced (invalid address).",
      complaint: "Stopped: the prospect complained.",
      suppressed: "Stopped: the address is on the suppression list.",
      lost: "Stopped: the opportunity is lost.",
      won: "Stopped: the opportunity is won.",
      customer: "Stopped: the lead became a customer.",
      do_not_call: "Stopped: the lead is « Do not call ».",
      rep_cancel: "Cancelled by the rep.",
      merged: "Stopped: the lead was merged.",
      ineligible: "Stopped: the lead can no longer get follow-up emails.",
      erased: "Stopped: personal data was erased.",
      disabled: "AI follow-ups turned off.",
      // GAVION video-campaign: a campaign follow-up's own stops
      not_interested: "Stopped: the lead is « Not interested ».",
      demo_booked: "Stopped: a demo is booked.",
      in_pipeline: "Stopped: the lead is in the pipeline.",
      deal_created: "Stopped: a deal was created.",
    },
    // why a touch was skipped (ai_follow_up_touches.error); any other code (an AI failure) → other
    touch_errors: {
      rejected: "Draft rejected.",
      expired: "Draft expired without a decision.",
      superseded: "Draft replaced.",
      failed: "Draft not sent.",
      overtaken: "Overtaken by the next touch.",
      abandoned: "Writing interrupted.",
      draft_gone: "The draft no longer exists.",
      moved_past: "Its new date had already passed.",
      cap_reached: "Daily AI draft cap reached.",
      too_old: "Too late: not written.",
      other: "The AI could not write this touch.",
    },
    cancel_plan: "Cancel the follow-up",
    cancel_confirm: "Confirm the cancellation",
    cancel_keep: "Keep the follow-up",
    cancelled: "Follow-up cancelled.",
    move: "Move",
    move_label: "New date",
    move_save: "Save the date",
    cancel_edit: "Cancel",
    moved: "Touch moved.",
    write_now: "Write now",
    written_now: "The touch will be written within the minute.",
    dialer_toast:
      "AI follow-up planned: the first touch will arrive in Drafts.",
    errors: {
      forbidden: "Only the follow-up's rep or an admin can change it.",
      not_found: "This follow-up no longer exists.",
      not_active: "This follow-up is already done or stopped.",
      not_planned: "This touch is no longer planned.",
      in_past: "Choose a date after today.",
      too_far: "Choose a date within the next 60 days.",
      not_business_day: "Choose a business day (no weekend, no holiday).",
      callback_day: "A callback is scheduled that day: choose another day.",
      out_of_order:
        "The date must stay between the previous and the next touch.",
      busy: "A touch is already being written or awaiting approval.",
      not_next: "Only the next touch can be written now.",
    },
    settings: {
      title: "AI follow-ups",
      hint: "After a « Voicemail left », « Callback agreed » or « Brochure sent » call, or Granola demo notes, the AI plans a few follow-up emails and writes each one an hour before it is due (10:00, business days). The rep approves each email in Drafts.",
      enabled: "AI follow-ups on",
      enabled_hint:
        "Turning this off stops every follow-up in progress at once (pending follow-up drafts leave Drafts). Turning it back on only applies to new calls and demos.",
      demo: "Follow-ups after a demo",
      cadences: "Cadences (business days after the call, comma-separated)",
      cadence_hint:
        "3 to 5 touches (callback agreed: 1 to 5, plus a touch two business days before a callback more than a week away), each between 1 and 60 days, in increasing order.",
      models: "Model per use",
      model_follow_up: "Follow-ups",
      model_drafts: "Drafts (on demand and proposed drafts)",
      model_names: {
        "claude-sonnet-5-5": "Claude Sonnet 5.5",
        "claude-haiku-5-5": "Claude Haiku 5.5",
      },
      playbook: "Sales playbook",
      playbook_hint:
        "The rules and the approach per situation the AI follows for each follow-up email.",
      playbook_reset: "Restore the default playbook",
      save: "Save the follow-ups",
      saved: "Follow-up settings saved.",
      save_failed: "Could not save. Try again.",
      errors: {
        playbook: "Between 1 and 8,000 characters.",
        cadence:
          "Increasing whole numbers between 1 and 60, in the allowed count.",
      },
    },
  },
  // GAVION gavion-context: Settings → AI « Gavion context » (admins)
  company_context: {
    settings: {
      title: "Gavion context",
      hint: "Everything the AI knows about Gavion and Gala (offer, plans, terms, data, objections): it reads it before every email (follow-ups and drafts). Reps read it in Training → « All about Gavion ».",
      context: "Context (Markdown)",
      count: "%{count} / %{max} characters",
      reset: "Restore the default context",
      facts: "Verified facts",
      facts_hint:
        "The only figures about Gavion an AI email may state (with the catalog's offer). A figure missing here is refused.",
      fact_text: "Fact",
      fact_source: "Source",
      fact_add: "Add a fact",
      fact_remove: "Remove this fact",
      facts_reset: "Restore the default facts",
      facts_count: "%{count} / %{max} facts",
      save: "Save the context",
      saved: "Gavion context saved.",
      save_failed: "Could not save. Try again.",
      errors: {
        context: "Between 1 and 30,000 characters.",
        facts:
          "At most 150 facts, each with a text of at most 500 characters and a source of at most 300 characters.",
      },
    },
  },
  settings: {
    title: "AI",
    style: {
      title: "My style",
      hint: "The assistant writes like you: your style is learned from your sent emails (Outlook) and refreshed every week. You can correct it.",
      label: "Style profile",
      default:
        "No profile yet: the default Gavion style applies (polite, short, warm, one clear ask).",
      excerpts: "Kept excerpts",
      built:
        "Updated on %{date} from %{smart_count} email |||| Updated on %{date} from %{smart_count} emails",
      save: "Save",
      saved: "Style saved.",
      rebuild: "Update my style",
      rebuilt:
        "Style updated from %{smart_count} email. |||| Style updated from %{smart_count} emails.",
      too_few:
        "Fewer than 5 sent emails found: the default Gavion style applies.",
      empty: "The profile cannot be empty.",
    },
    admin: {
      title: "AI assistant (admins)",
      product_brief: "Gavion description used by the AI",
      brochure_url: "Brochure link (https)",
      brochure_hint:
        "Used only when the « Brochure » template is missing or has no link.",
      daily_cap: "AI drafts per rep per day",
      stale_days: "Lead « gone quiet » after (days)",
      save: "Save",
      saved: "AI settings saved.",
      save_failed: "Could not save. Try again.",
      errors: {
        productBrief: "Between 1 and 2,000 characters.",
        brochureUrl: "An https:// address or nothing.",
        dailyCap: "A whole number from 0 to 5,000.",
        staleDays: "A whole number from 1 to 90.",
      },
    },
    auto_send: {
      title: "Automatic sending",
      hint: "Coming soon: a trigger will be able to send without confirmation. Meanwhile, here is the share of drafts sent without edits.",
      label: "Send automatically — %{trigger}",
      soon: "Coming soon",
      metric:
        "%{rate}% sent without edits (30 days, %{smart_count} decided) |||| %{rate}% sent without edits (30 days, %{smart_count} decided)",
      error: "Metric unavailable for now.",
      no_data: "No decided draft yet (30 days).",
    },
  },
};
